import api from './api';

// Helper para construir URL de imagem (suporta tanto local quanto S3)
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Se já é uma URL completa (S3), retorna como está
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // Se é path relativo, constrói URL do backend
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${API_URL}/${imageUrl}`;
};

export const authService = {
  // Signup de responsável
  signupParent: (name, email, password, birthdate, cep, city, state) => {
    return api.post('/auth/signup', { name, email, password, birthdate, cep, city, state });
  },

  // Verificar email com código
  verifyEmail: (email, verificationCode) => {
    return api.post('/auth/verify-email', { email, verification_code: verificationCode });
  },

  // Login de responsável
  loginParent: (email, password) => {
    return api.post('/auth/login', { email, password });
  },

  // Login de criança via PIN + pairing code
  loginChild: (pairingCode, pin) => {
    return api.post('/auth/child/login', { pairing_code: pairingCode, pin });
  },

  // Esqueci minha senha - solicita código
  forgotPassword: (email) => {
    return api.post('/auth/forgot-password', { email });
  },

  // Redefinir senha com código
  resetPassword: (email, resetCode, newPassword) => {
    return api.post('/auth/reset-password', { 
      email, 
      reset_code: resetCode, 
      new_password: newPassword 
    });
  },

  // Logout (local)
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_type');
  },
};

export const childrenService = {
  // Listar crianças do responsável
  getChildren: () => {
    return api.get('/children');
  },

  // Obter perfil da criança autenticada
  getMyProfile: () => {
    return api.get('/children/profile/me');
  },

  // Obter info de badges/patentes da criança autenticada
  getMyBadges: () => {
    return api.get('/children/profile/me/badges');
  },

  // Criar criança
  createChild: (name, pin, birthdate) => {
    return api.post('/children', { name, pin, birthdate });
  },

  // Atualizar criança
  updateChild: (childId, data) => {
    return api.patch(`/children/${childId}`, data);
  },
};

export const tasksService = {
  // Listar tarefas de uma criança
  getTasks: (childId) => {
    return api.get('/tasks', { params: { child_id: childId } });
  },

  // Listar tarefas de hoje
  getTodayTasks: () => {
    return api.get('/tasks/today');
  },

  // Listar conclusões de tarefas para uma criança
  getTaskCompletions: (childId) => {
    return api.get('/task-completions', { params: { child_id: childId } });
  },

  // Criar tarefa
  createTask: (childId, title, description, points, category, frequency, parent_review_only = false) => {
    return api.post('/tasks', {
      child_id: childId,
      title,
      description,
      points,
      category,
      frequency,
      parent_review_only,
    });
  },

  // Atualizar tarefa
  updateTask: (taskId, data) => {
    return api.patch(`/tasks/${taskId}`, data);
  },

  // Deletar tarefa
  deleteTask: (taskId) => {
    return api.delete(`/tasks/${taskId}`);
  },
};

export const completionsService = {
  // Enviar conclusão de tarefa com imagem
  createCompletion: (taskId, imageFile) => {
    const formData = new FormData();
    formData.append('task_id', taskId);
    formData.append('image', imageFile);

    return api.post('/task-completions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Listar conclusões por status
  getCompletions: (status) => {
    return api.get('/task-completions', { params: { status } });
  },

  // Aprovar conclusão
  approveCompletion: (completionId, parentComment) => {
    return api.patch(`/task-completions/${completionId}/approve`, {
      parent_comment: parentComment || '',
    });
  },

  // Rejeitar conclusão
  rejectCompletion: (completionId, parentComment) => {
    return api.patch(`/task-completions/${completionId}/reject`, {
      parent_comment: parentComment || '',
    });
  },
};

export const rewardsService = {
  // Listar recompensas
  getRewards: () => {
    return api.get('/rewards');
  },

  // Criar recompensa
  createReward: (title, description, costPoints, minAge) => {
    return api.post('/rewards', {
      title,
      description,
      cost_points: costPoints,
      min_age: minAge,
    });
  },

  // Adicionar recompensa para uma criança
  addRewardToChild: (rewardId, childId) => {
    return api.post(`/rewards/${rewardId}/children/${childId}/add`);
  },

  // Remover recompensa de uma criança
  removeRewardFromChild: (rewardId, childId) => {
    return api.delete(`/rewards/${rewardId}/children/${childId}/remove`);
  },

  // Listar crianças com acesso a uma recompensa
  getRewardChildren: (rewardId) => {
    return api.get(`/rewards/${rewardId}/children`);
  },

  // Atualizar recompensa
  updateReward: (rewardId, data) => {
    return api.patch(`/rewards/${rewardId}`, data);
  },

  // Deletar recompensa
  deleteReward: (rewardId) => {
    return api.delete(`/rewards/${rewardId}`);
  },

  // Resgatar recompensa
  redeemReward: (rewardId) => {
    return api.post(`/rewards/${rewardId}/redeem`);
  },

  // Listar resgates por status
  getRedemptions: (status) => {
    return api.get('/reward-redemptions', { params: { status } });
  },

  // Marcar resgate como entregue
  deliverRedemption: (redemptionId) => {
    return api.patch(`/reward-redemptions/${redemptionId}/deliver`);
  },

  // Listar resgates da criança
  getMyRedemptions: () => {
    return api.get('/reward-redemptions/my-redemptions');
  },

  // Confirmar recebimento de recompensa
  confirmRedemption: (redemptionId) => {
    return api.patch(`/reward-redemptions/${redemptionId}/confirm`);
  },
};

export const notificationsService = {
  // Obter notificações do usuário autenticado
  getNotifications: () => {
    return api.get('/notifications');
  },

  // Marcar notificação como lida
  markNotificationRead: (notificationId) => {
    return api.patch(`/notifications/${notificationId}/read`);
  },
};

export const challengesService = {
  // Criar desafio
  createChallenge: (childId, title, description, points) => {
    return api.post('/challenges', { child_id: childId, title, description, points });
  },

  // Listar desafios
  getChallenges: (status, createdByRole) => {
    return api.get('/challenges', { params: { status, created_by_role: createdByRole } });
  },

  // Obter detalhes de um desafio
  getChallenge: (challengeId) => {
    return api.get(`/challenges/${challengeId}`);
  },

  // Aceitar desafio
  acceptChallenge: (challengeId) => {
    return api.patch(`/challenges/${challengeId}/accept`);
  },

  // Rejeitar desafio
  rejectChallenge: (challengeId) => {
    return api.patch(`/challenges/${challengeId}/reject`);
  },

  // Cancelar desafio
  cancelChallenge: (challengeId) => {
    return api.patch(`/challenges/${challengeId}/cancel`);
  },
};

export const eventsService = {
  getChildEvents: (childId, params) => {
    return api.get(`/children/${childId}/events`, { params });
  },

  getChildUpcomingEvents: (childId, days = 14) => {
    return api.get(`/children/${childId}/events/upcoming`, { params: { days } });
  },

  createChildEvent: (childId, payload) => {
    return api.post(`/children/${childId}/events`, payload);
  },

  updateChildEvent: (childId, eventId, payload) => {
    return api.put(`/children/${childId}/events/${eventId}`, payload);
  },

  deleteChildEvent: (childId, eventId) => {
    return api.delete(`/children/${childId}/events/${eventId}`);
  },

  getMyEvents: (params) => {
    return api.get('/me/events', { params });
  },

  getMyUpcomingEvents: (days = 14) => {
    return api.get('/me/events/upcoming', { params: { days } });
  }
};

